// =============================================================================
// OELOX — API Client
// =============================================================================
// Typed Supabase client wrapper. All apps consume Supabase through this module.
// Raw untyped queries are never used directly from any app.
// =============================================================================

import { createClient, type SupabaseClient } from "@supabase/supabase-js";

// Database type will be generated from Supabase schema when DB is live.
// Run: npx supabase gen types typescript --project-id <id> > packages/types/src/supabase.ts
// Then uncomment the import below and remove this `any` declaration.
// import type { Database } from "@veorest/types/supabase";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Database = any;

let supabaseInstance: SupabaseClient<Database> | null = null;

function readBrowserEnv(name: string): string | undefined {
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  if (env?.[name]) return env[name];

  if (typeof window === "undefined") return undefined;

  const runtimeConfig = (window as Window & {
    OELOX_PUBLIC_CONFIG?: {
      VITE_SUPABASE_URL?: string;
      VITE_SUPABASE_ANON_KEY?: string;
      supabaseUrl?: string;
      supabaseAnonKey?: string;
    };
  }).OELOX_PUBLIC_CONFIG;

  if (name === "VITE_SUPABASE_URL") {
    return runtimeConfig?.VITE_SUPABASE_URL ?? runtimeConfig?.supabaseUrl;
  }

  if (name === "VITE_SUPABASE_ANON_KEY") {
    return runtimeConfig?.VITE_SUPABASE_ANON_KEY ?? runtimeConfig?.supabaseAnonKey;
  }

  return undefined;
}

export function getSupabaseClient(): SupabaseClient<Database> {
  if (supabaseInstance) return supabaseInstance;

  const url = readBrowserEnv("VITE_SUPABASE_URL");
  const anonKey = readBrowserEnv("VITE_SUPABASE_ANON_KEY");

  // No hardcoded credential fallbacks: configuration must come from the
  // environment. Only the public anon key and URL are ever permitted in the
  // browser. Fail loudly rather than silently connecting to a baked-in project.
  if (!url || !anonKey || url.includes("your-project")) {
    throw new Error(
      "Missing Supabase configuration. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY " +
        "(public anon key only — never the service-role key) in the environment."
    );
  }

  supabaseInstance = createClient<Database>(url, anonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  });

  return supabaseInstance;
}

// ── Platform admin authorization (RLS-enforced server-side check) ─────────────

export async function verifyActivePlatformAdmin(
  userId: string
): Promise<{ authorized: boolean; role?: string }> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("platform_admins")
    .select("role")
    .eq("user_id", userId)
    .eq("is_active", true)
    .maybeSingle();

  if (error || !data) {
    return { authorized: false };
  }

  return { authorized: true, role: data.role };
}

export * from "./admin";

// ── TanStack React Query Defaults ────────────────────────────────────────────

import { QueryClient, type QueryClientConfig } from "@tanstack/react-query";

export const defaultQueryConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error) => {
        // Skip retries when offline — let IndexedDB cache kick in immediately
        if (typeof navigator !== "undefined" && !navigator.onLine) return false;
        // Network errors don't benefit from retry (backend unreachable)
        if (error instanceof TypeError || (error as any)?.message?.includes("fetch")) return false;
        return failureCount < 1;
      },
      retryDelay: 800,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
};

export function createQueryClient(
  config?: Partial<QueryClientConfig>
): QueryClient {
  return new QueryClient({
    ...defaultQueryConfig,
    ...config,
    defaultOptions: {
      ...defaultQueryConfig.defaultOptions,
      ...config?.defaultOptions,
    },
  });
}

// ── Auth State (Zustand) ─────────────────────────────────────────────────────

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { migrateLocalStorageKey } from "@veorest/utils";
import type { User, Session } from "@supabase/supabase-js";

migrateLocalStorageKey("oelox-auth", "veorest-auth");

interface AuthState {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  setSession: (session: Session | null) => void;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  signOut: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      session: null,
      isLoading: true,
      setSession: (session) => set({ session }),
      setUser: (user) => set({ user }),
      setLoading: (isLoading) => set({ isLoading }),
      signOut: async () => {
        const client = getSupabaseClient();
        await client.auth.signOut();
        set({ user: null, session: null });
      },
    }),
    {
      name: "oelox-auth",
      partialize: (state) => ({ session: state.session, user: state.user }),
    }
  )
);

export * from "./customers";
export * from "./menu";
export * from "./orders";
export * from "./loyalty";
export * from "./drivers";
export * from "./tenant";
export * from "./inbox";
export * from "./wallet";
